import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, List, Clock, Plus, ChevronLeft, ChevronRight, 
  Heart, RotateCcw, Film, Tv, X, Search, Tag, Lock, Users, Globe,
  Edit2, Trash2, Trophy
} from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import AwardRating, { AwardRatingInline } from './AwardRating';
import axios from 'axios';
import { API_URL } from '../config/api';
import { searchMulti, getImageUrl } from '../services/tmdb';

const posterFallback = (e) => {
  e.target.onerror = null;
  e.target.style.display = 'none';
  const parent = e.target.parentElement;
  if (parent && !parent.querySelector('.poster-fallback')) {
    const fb = document.createElement('div');
    fb.className = 'poster-fallback w-full h-full flex items-center justify-center bg-zinc-700 rounded text-gray-400';
    fb.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
    parent.appendChild(fb);
  }
};

// ==================== DIARY ENTRY MODAL ====================
const DiaryEntryModal = ({ isOpen, onClose, user, onEntryAdded, editEntry = null }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [formData, setFormData] = useState({
    watched_date: new Date().toISOString().split('T')[0],
    rating: 0,
    review: '',
    tags: '',
    privacy: 'public',
    rewatch: false,
    liked: false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editEntry) {
      setSelectedContent({
        id: editEntry.tmdb_id,
        title: editEntry.title,
        name: editEntry.title,
        poster_path: editEntry.poster_path,
        media_type: editEntry.media_type
      });
      setFormData({
        watched_date: editEntry.watched_date,
        rating: editEntry.rating || 0,
        review: editEntry.review || '',
        tags: (editEntry.tags || []).join(', '),
        privacy: editEntry.privacy || 'public',
        rewatch: editEntry.rewatch || false,
        liked: editEntry.liked || false
      });
    } else {
      setSelectedContent(null);
      setFormData({
        watched_date: new Date().toISOString().split('T')[0],
        rating: 0,
        review: '',
        tags: '',
        privacy: 'public',
        rewatch: false,
        liked: false
      });
    }
    setSearchQuery('');
    setSearchResults([]);
  }, [editEntry, isOpen]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const results = await searchMulti(query);
      setSearchResults(results.filter(r => r.media_type === 'movie' || r.media_type === 'tv').slice(0, 10));
    } catch (error) {
      console.error('Search failed:', error);
    }
    setSearching(false);
  };

  const handleSelectContent = (content) => {
    setSelectedContent(content);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSave = async () => {
    if (!selectedContent) {
      setError('Please select a film or TV show');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const params = {
        user_id: user.id,
        tmdb_id: selectedContent.id,
        media_type: selectedContent.media_type || 'movie',
        title: selectedContent.title || selectedContent.name,
        poster_path: selectedContent.poster_path || '',
        backdrop_path: selectedContent.backdrop_path || '',
        year: selectedContent.release_date?.slice(0, 4) || selectedContent.first_air_date?.slice(0, 4) || null,
        watched_date: formData.watched_date,
        rewatch: formData.rewatch,
        liked: formData.liked,
        rating: formData.rating || null,
        review: formData.review,
        tags: formData.tags,
        privacy: formData.privacy
      };

      if (editEntry) {
        await axios.put(`${API_URL}/api/diary/${editEntry.id}`, null, {
          params: {
            user_id: user.id,
            watched_date: formData.watched_date,
            rewatch: formData.rewatch,
            liked: formData.liked,
            rating: formData.rating || null,
            review: formData.review,
            tags: formData.tags,
            privacy: formData.privacy
          }
        });
      } else {
        await axios.post(`${API_URL}/api/diary`, null, { params });
      }

      onEntryAdded?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save entry');
    }
    setSaving(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-zinc-900 text-white border-zinc-700 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{editEntry ? 'Edit Chronicle Entry' : 'Log a Film'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Film Search */}
        {!editEntry && (
          <div className="mb-4">
            <Label className="text-gray-300 mb-2 block">Film / TV Show</Label>
            {selectedContent ? (
              <div className="flex items-center gap-3 bg-zinc-800 p-3 rounded-lg">
                <img
                  src={getImageUrl(selectedContent.poster_path, 'w92')}
                  alt={selectedContent.title || selectedContent.name}
                  className="w-12 h-18 object-cover rounded"
                  onError={posterFallback}
                />
                <div className="flex-1">
                  <p className="font-medium">{selectedContent.title || selectedContent.name}</p>
                  <p className="text-sm text-gray-400">
                    {selectedContent.media_type === 'tv' ? 'TV Series' : 'Movie'}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedContent(null)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search for a film or TV show..."
                  className="bg-zinc-800 border-zinc-700 text-white pl-10"
                />
                
                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                    {searchResults.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => handleSelectContent(result)}
                        className="w-full flex items-center gap-3 p-2 hover:bg-zinc-700 text-left"
                      >
                        <img
                          src={getImageUrl(result.poster_path, 'w92')}
                          alt={result.title || result.name}
                          className="w-10 h-14 object-cover rounded"
                          onError={posterFallback}
                        />
                        <div>
                          <p className="text-white text-sm">{result.title || result.name}</p>
                          <p className="text-gray-500 text-xs">
                            {result.media_type === 'tv' ? 'TV' : 'Movie'} • {
                              (result.release_date || result.first_air_date || '').slice(0, 4)
                            }
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {searching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Date Watched */}
        <div className="mb-4">
          <Label className="text-gray-300 mb-2 block">Date Watched</Label>
          <Input
            type="date"
            value={formData.watched_date}
            onChange={(e) => setFormData({ ...formData, watched_date: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white"
          />
        </div>

        {/* Rating */}
        <div className="mb-4">
          <Label className="text-gray-300 mb-2 block">Rating</Label>
          <AwardRating
            value={formData.rating}
            onChange={(rating) => setFormData({ ...formData, rating })}
            size="lg"
            showLabel
          />
        </div>

        {/* Rewatch & Liked */}
        <div className="flex gap-4 mb-4">
          <button
            onClick={() => setFormData({ ...formData, rewatch: !formData.rewatch })}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              formData.rewatch 
                ? 'bg-blue-600/20 border-blue-500 text-blue-400' 
                : 'border-zinc-700 text-gray-400 hover:border-zinc-500'
            }`}
          >
            <RotateCcw className="h-4 w-4" />
            Rewatch
          </button>
          <button
            onClick={() => setFormData({ ...formData, liked: !formData.liked })}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              formData.liked 
                ? 'bg-red-600/20 border-red-500 text-red-400' 
                : 'border-zinc-700 text-gray-400 hover:border-zinc-500'
            }`}
          >
            <Heart className={`h-4 w-4 ${formData.liked ? 'fill-current' : ''}`} />
            Liked
          </button>
        </div>

        {/* Review */}
        <div className="mb-4">
          <Label className="text-gray-300 mb-2 block">Review (optional)</Label>
          <Textarea
            value={formData.review}
            onChange={(e) => setFormData({ ...formData, review: e.target.value })}
            placeholder="What did you think?"
            className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
          />
        </div>

        {/* Tags */}
        <div className="mb-4">
          <Label className="text-gray-300 mb-2 block">Tags</Label>
          <div className="relative">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="cinema, date night, comfort watch (comma separated)"
              className="bg-zinc-800 border-zinc-700 text-white pl-10"
            />
          </div>
        </div>

        {/* Privacy */}
        <div className="mb-6">
          <Label className="text-gray-300 mb-2 block">Privacy</Label>
          <div className="flex gap-2">
            {[
              { value: 'public', icon: Globe, label: 'Public' },
              { value: 'friends', icon: Users, label: 'Friends' },
              { value: 'private', icon: Lock, label: 'Private' }
            ].map(({ value, icon: Icon, label }) => (
              <button
                key={value}
                onClick={() => setFormData({ ...formData, privacy: value })}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                  formData.privacy === value 
                    ? 'bg-red-600/20 border-red-500 text-red-400' 
                    : 'border-zinc-700 text-gray-400 hover:border-zinc-500'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

        <Button
          onClick={handleSave}
          disabled={saving || (!editEntry && !selectedContent)}
          className="w-full bg-red-600 hover:bg-red-700"
        >
          {saving ? 'Saving...' : (editEntry ? 'Update Entry' : 'Add to Chronicle')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// ==================== DIARY ENTRY CARD ====================
const DiaryEntryCard = ({ entry, onEdit, onDelete, compact = false }) => {
  const title = entry.title;
  const posterUrl = getImageUrl(entry.poster_path, 'w185');

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-2 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors">
        <img src={posterUrl} alt={title} className="w-10 h-14 object-cover rounded" onError={posterFallback} />
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">{title}</p>
          {entry.rating > 0 && <AwardRatingInline value={entry.rating} size="xs" />}
        </div>
        {entry.liked && <Heart className="h-4 w-4 text-red-500 fill-current flex-shrink-0" />}
      </div>
    );
  }

  return (
    <div className="bg-zinc-800 rounded-lg overflow-hidden hover:ring-2 hover:ring-red-600/50 transition-all group">
      <div className="relative aspect-[2/3]">
        <img src={posterUrl} alt={title} className="w-full h-full object-cover" onError={posterFallback} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <div className="flex gap-2">
              <button
                onClick={() => onEdit?.(entry)}
                className="p-2 bg-zinc-700 rounded-full hover:bg-zinc-600"
              >
                <Edit2 className="h-4 w-4" />
              </button>
              <button
                onClick={() => onDelete?.(entry)}
                className="p-2 bg-red-600/80 rounded-full hover:bg-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Badges */}
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          {entry.liked && (
            <div className="bg-red-600 p-1.5 rounded-full">
              <Heart className="h-3 w-3 fill-current" />
            </div>
          )}
          {entry.rewatch && (
            <div className="bg-blue-600 p-1.5 rounded-full">
              <RotateCcw className="h-3 w-3" />
            </div>
          )}
        </div>
      </div>
      
      <div className="p-3">
        <p className="text-white font-medium text-sm line-clamp-1">{title}</p>
        <div className="flex items-center justify-between mt-1">
          <p className="text-gray-500 text-xs">{entry.watched_date}</p>
          {entry.rating > 0 && <AwardRatingInline value={entry.rating} size="xs" />}
        </div>
        {entry.tags && entry.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {entry.tags.slice(0, 2).map((tag, i) => (
              <span key={i} className="text-xs bg-zinc-700 text-gray-400 px-1.5 py-0.5 rounded">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== CALENDAR VIEW ====================
const CalendarView = ({ user, entries, year, month, onMonthChange, onEdit, onDelete }) => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];

  // Group entries by date
  const entriesByDate = {};
  entries.forEach(entry => {
    const date = entry.watched_date;
    if (!entriesByDate[date]) entriesByDate[date] = [];
    entriesByDate[date].push(entry);
  });

  const days = [];
  // Empty cells for days before first of month
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="aspect-square" />);
  }
  // Days of month
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayEntries = entriesByDate[dateStr] || [];
    const isToday = dateStr === new Date().toISOString().split('T')[0];

    days.push(
      <div
        key={day}
        className={`aspect-square p-1 border border-zinc-800 rounded-lg ${
          isToday ? 'ring-2 ring-red-600' : ''
        } ${dayEntries.length > 0 ? 'bg-zinc-800' : ''}`}
      >
        <div className="text-xs text-gray-500 mb-1">{day}</div>
        <div className="flex flex-wrap gap-0.5">
          {dayEntries.slice(0, 4).map((entry, i) => (
            <img
              key={i}
              src={getImageUrl(entry.poster_path, 'w92')}
              alt={entry.title}
              className="w-6 h-8 object-cover rounded cursor-pointer hover:ring-2 hover:ring-red-500"
              onClick={() => onEdit?.(entry)}
              title={entry.title}
              onError={posterFallback}
            />
          ))}
          {dayEntries.length > 4 && (
            <div className="w-6 h-8 bg-zinc-700 rounded flex items-center justify-center text-xs text-gray-400">
              +{dayEntries.length - 4}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => onMonthChange(month === 1 ? year - 1 : year, month === 1 ? 12 : month - 1)}
          className="p-2 hover:bg-zinc-800 rounded-lg"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h3 className="text-xl font-semibold">{monthNames[month - 1]} {year}</h3>
        <button
          onClick={() => onMonthChange(month === 12 ? year + 1 : year, month === 12 ? 1 : month + 1)}
          className="p-2 hover:bg-zinc-800 rounded-lg"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-xs text-gray-500 font-medium py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {days}
      </div>
    </div>
  );
};

// ==================== TIMELINE VIEW ====================
const TimelineView = ({ entries, onEdit, onDelete }) => {
  // Group entries by month
  const groupedByMonth = {};
  entries.forEach(entry => {
    const monthKey = entry.watched_date.slice(0, 7); // "YYYY-MM"
    if (!groupedByMonth[monthKey]) groupedByMonth[monthKey] = [];
    groupedByMonth[monthKey].push(entry);
  });

  const sortedMonths = Object.keys(groupedByMonth).sort().reverse();

  return (
    <div className="space-y-8">
      {sortedMonths.map(monthKey => {
        const [year, month] = monthKey.split('-');
        const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'long' });
        const monthEntries = groupedByMonth[monthKey];

        return (
          <div key={monthKey}>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-red-500" />
              {monthName} {year}
              <span className="text-gray-500 text-sm font-normal">({monthEntries.length} films)</span>
            </h3>
            
            <div className="relative pl-6 border-l-2 border-zinc-700">
              {monthEntries.map((entry, index) => (
                <div key={entry.id} className="relative mb-6 last:mb-0">
                  {/* Timeline dot */}
                  <div className="absolute -left-8 w-4 h-4 bg-red-600 rounded-full border-4 border-zinc-900" />
                  
                  {/* Entry Card */}
                  <div className="bg-zinc-800 rounded-lg p-4 ml-2">
                    <div className="flex gap-4">
                      <img
                        src={getImageUrl(entry.poster_path, 'w154')}
                        alt={entry.title}
                        className="w-20 h-28 object-cover rounded"
                        onError={posterFallback}
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-white font-medium">{entry.title}</h4>
                            <p className="text-gray-500 text-sm">{entry.watched_date}</p>
                          </div>
                          <div className="flex gap-2">
                            {entry.liked && <Heart className="h-4 w-4 text-red-500 fill-current" />}
                            {entry.rewatch && <RotateCcw className="h-4 w-4 text-blue-500" />}
                          </div>
                        </div>
                        
                        {entry.rating > 0 && (
                          <div className="mt-2">
                            <AwardRatingInline value={entry.rating} />
                          </div>
                        )}
                        
                        {entry.review && (
                          <p className="text-gray-400 text-sm mt-2 line-clamp-2">{entry.review}</p>
                        )}
                        
                        {entry.tags && entry.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {entry.tags.map((tag, i) => (
                              <span key={i} className="text-xs bg-zinc-700 text-gray-400 px-2 py-0.5 rounded">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => onEdit?.(entry)}
                            className="text-gray-400 hover:text-white text-sm flex items-center gap-1"
                          >
                            <Edit2 className="h-3 w-3" /> Edit
                          </button>
                          <button
                            onClick={() => onDelete?.(entry)}
                            className="text-gray-400 hover:text-red-500 text-sm flex items-center gap-1"
                          >
                            <Trash2 className="h-3 w-3" /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      
      {sortedMonths.length === 0 && (
        <div className="text-center py-12">
          <Film className="h-12 w-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No chronicle entries yet</p>
          <p className="text-gray-500 text-sm">Start logging films to see your timeline</p>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN DIARY COMPONENT ====================
const Diary = ({ user, isOpen, onClose }) => {
  const [view, setView] = useState('list'); // 'calendar', 'list', 'timeline'
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Calendar state
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);

  const fetchEntries = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/diary/${user.id}?viewer_id=${user.id}&limit=200`);
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch diary:', error);
    }
    setLoading(false);
  }, [user]);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_URL}/api/diary/${user.id}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && user) {
      fetchEntries();
      fetchStats();
    }
  }, [isOpen, user, fetchEntries, fetchStats]);

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete "${entry.title}" from your chronicle?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/diary/${entry.id}?user_id=${user.id}`);
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const handleEdit = (entry) => {
    setEditEntry(entry);
    setShowAddModal(true);
  };

  const handleEntryAdded = () => {
    fetchEntries();
    fetchStats();
    setEditEntry(null);
  };

  const handleMonthChange = (year, month) => {
    setCalendarYear(year);
    setCalendarMonth(month);
  };

  // Filter entries for calendar view
  const calendarEntries = entries.filter(e => {
    const [y, m] = e.watched_date.split('-');
    return parseInt(y) === calendarYear && parseInt(m) === calendarMonth;
  });

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-zinc-900 text-white border-zinc-700 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-600 rounded-lg">
              <Film className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Chronicle</h2>
              {stats && (
                <p className="text-gray-400 text-sm">
                  {stats.total_entries} films logged • {stats.average_rating > 0 ? `${stats.average_rating} avg rating` : 'No ratings yet'}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* View Toggle & Add Button */}
        <div className="flex items-center justify-between py-4">
          <div className="flex bg-zinc-800 rounded-lg p-1">
            {[
              { id: 'list', icon: List, label: 'List' },
              { id: 'calendar', icon: Calendar, label: 'Calendar' },
              { id: 'timeline', icon: Clock, label: 'Timeline' }
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setView(id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  view === id ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="text-sm">{label}</span>
              </button>
            ))}
          </div>

          <Button
            onClick={() => { setEditEntry(null); setShowAddModal(true); }}
            className="bg-red-600 hover:bg-red-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Log Film
          </Button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
            </div>
          ) : (
            <>
              {/* List View */}
              {view === 'list' && (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {entries.map(entry => (
                    <DiaryEntryCard
                      key={entry.id}
                      entry={entry}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                  {entries.length === 0 && (
                    <div className="col-span-full text-center py-12">
                      <Film className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400">No films logged yet</p>
                      <Button
                        onClick={() => setShowAddModal(true)}
                        className="mt-4 bg-red-600 hover:bg-red-700"
                      >
                        Log Your First Film
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Calendar View */}
              {view === 'calendar' && (
                <CalendarView
                  user={user}
                  entries={calendarEntries}
                  year={calendarYear}
                  month={calendarMonth}
                  onMonthChange={handleMonthChange}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              )}

              {/* Timeline View */}
              {view === 'timeline' && (
                <TimelineView
                  entries={entries}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              )}
            </>
          )}
        </div>

        {/* Diary Entry Modal */}
        <DiaryEntryModal
          isOpen={showAddModal}
          onClose={() => { setShowAddModal(false); setEditEntry(null); }}
          user={user}
          onEntryAdded={handleEntryAdded}
          editEntry={editEntry}
        />
      </DialogContent>
    </Dialog>
  );
};

export default Diary;
